import { MaintenanceResponse } from './models/maintenance-state.model';

// ─── CONTROLES PRINCIPAIS ────────────────────────────────────────────────────
// MOCK_ENABLED        : false → service chama o endpoint real
// SCENE_DURATION_S    : duração de cada cena em segundos (muda tudo de uma vez)
// TIME_OFFSET_MIN     : offset base para startsAt/endsAt em minutos
// MOCK_POLL_INTERVAL_MS: intervalo fixo de polling quando mock está ativo.
//                        Substitui o adaptativo do service para que cenas curtas
//                        sejam observadas independente da fase.
//
// Debug rápido : SCENE_DURATION_S = 20, TIME_OFFSET_MIN = 3, MOCK_POLL_INTERVAL_MS = 8_000
// Produção     : SCENE_DURATION_S = 300, TIME_OFFSET_MIN = 30, MOCK_POLL_INTERVAL_MS = 30_000

export const MOCK_ENABLED = false;

const SCENE_DURATION_S     = 20;    // segundos por cena
const TIME_OFFSET_MIN      = 3;     // minutos base para timestamps

/** Intervalo fixo de polling usado pelo service quando MOCK_ENABLED = true.
 *  Exportado para que o service o consuma diretamente. */
export const MOCK_POLL_INTERVAL_MS = 8_000; // 8 segundos — garante ~2 polls por cena

// ─────────────────────────────────────────────────────────────────────────────

interface MockScene {
  label: string;
  durationSeconds: number;
  response: () => MaintenanceResponse | null; // null = simula erro de rede
}

// ─── ENGINE DO ROTEIRO ────────────────────────────────────────────────────────

let scriptStartedAt: number | null = null;
let lastLabel: string | null = null;

function getCurrentScene(): MockScene {
  if (!scriptStartedAt) scriptStartedAt = Date.now();
  const elapsedS = (Date.now() - scriptStartedAt) / 1000;
  let acc = 0;
  for (const scene of MOCK_SCRIPT) {
    acc += scene.durationSeconds;
    if (elapsedS < acc) {
      if (scene.label !== lastLabel) {
        lastLabel = scene.label;
      }
      return scene;
    }
  }
  return MOCK_SCRIPT[MOCK_SCRIPT.length - 1];
}

// ─── FUNÇÕES DE CENA ─────────────────────────────────────────────────────────
// Cada função retorna outra função — timestamps são recalculados a cada poll,
// nunca congelados no momento em que o array foi criado.

function nowIso(): string {
  return new Date().toISOString();
}

function isoInMinutes(offsetMinutes: number): string {
  return new Date(Date.now() + offsetMinutes * 60_000).toISOString();
}

function normalScene(): () => MaintenanceResponse {
  return () => ({
    phase: 'normal',
    platforms: {
      web:    { enabled: true, minVersion: '1.4.2' },
      mobile: { enabled: true, minVersion: '1.0.0' },
    },
    startsAt: null, endsAt: null,
    serverTime: nowIso(), message: null, releaseNotesUrl: null, bypassToken: null,
  });
}

function scheduledScene(minsUntilStart: number, minsUntilEnd: number): () => MaintenanceResponse {
  return () => ({
    phase: 'scheduled',
    platforms: {
      web:    { enabled: true, minVersion: '1.4.2' },
      mobile: { enabled: true, minVersion: '1.0.0' },
    },
    startsAt: isoInMinutes(minsUntilStart), endsAt: isoInMinutes(minsUntilEnd),
    serverTime: nowIso(),
    message: 'Manutenção programada para atualização do sistema.',
    releaseNotesUrl: null, bypassToken: null,
  });
}

function windingDownScene(minsUntilStart: number, minsUntilEnd: number): () => MaintenanceResponse {
  return () => ({
    phase: 'winding_down',
    platforms: {
      web:    { enabled: true, minVersion: '1.4.2' },
      mobile: { enabled: true, minVersion: '1.0.0' },
    },
    startsAt: isoInMinutes(minsUntilStart), endsAt: isoInMinutes(minsUntilEnd),
    serverTime: nowIso(),
    message: 'Sistema entrará em manutenção em alguns minutos. Finalize operações em andamento.',
    releaseNotesUrl: null, bypassToken: null,
  });
}

function maintenanceScene(minsUntilEnd: number): () => MaintenanceResponse {
  return () => ({
    phase: 'maintenance',
    platforms: {
      web:    { enabled: false, minVersion: '1.5.0' },
      mobile: { enabled: false, minVersion: '1.0.0' },
    },
    startsAt: isoInMinutes(-5), endsAt: isoInMinutes(minsUntilEnd),
    serverTime: nowIso(),
    message: 'Sistema em manutenção. Voltamos em breve.',
    releaseNotesUrl: null, bypassToken: null,
  });
}

function errorScene(): () => null {
  return () => null;
}

// ─── ROTEIRO COMPLETO ─────────────────────────────────────────────────────────
// Cada cena dura SCENE_DURATION_S segundos.
// TIME_OFFSET_MIN (= T) é o offset base; os cenários usam T e seus múltiplos.
//
// Para pausar em um cenário específico, substitua MOCK_SCRIPT por uma única cena:
//   const MOCK_SCRIPT: MockScene[] = [
//     { label: 'Debug fixo', durationSeconds: 999_999, response: windingDownScene(T, T * 20) },
//   ];

const D = SCENE_DURATION_S;
const T = TIME_OFFSET_MIN;

const MOCK_SCRIPT: MockScene[] = [
  // ── 1: Sistema normal ───────────────────────────────────────────────────────
  {
    label: '1 · Normal — início',
    durationSeconds: D,
    response: normalScene(),
  },

  // ── 2: Manutenção agendada com ETA longo ────────────────────────────────────
  {
    label: `2 · Scheduled — T+${T * 10}min para iniciar`,
    durationSeconds: D,
    response: scheduledScene(T * 10, T * 20),
  },

  // ── 3: ETA estendido (operador adiou) ───────────────────────────────────────
  {
    label: `3 · Scheduled — ETA aumentou para T+${T * 20}min (adiamento)`,
    durationSeconds: D,
    response: scheduledScene(T * 20, T * 40),
  },

  // ── 4: Cancelamento — banner some, sistema volta ao normal ──────────────────
  {
    label: '4 · Normal — manutenção cancelada (banner deve sumir)',
    durationSeconds: D,
    response: normalScene(),
  },

  // ── 5: Pausa normal (confirma ausência de banner por 2 polls) ────────────────
  {
    label: '5 · Normal — pausa estável',
    durationSeconds: D,
    response: normalScene(),
  },

  // ── 6: Nova janela agendada, ETA curto (polling acelerado) ──────────────────
  {
    label: `6 · Scheduled — T+${T}min para iniciar (ETA curto)`,
    durationSeconds: D,
    response: scheduledScene(T, T * 4),
  },

  // ── 7: Winding down ─────────────────────────────────────────────────────────
  {
    label: `7 · Winding Down — T+${Math.ceil(T / 2)}min para iniciar`,
    durationSeconds: D,
    response: windingDownScene(Math.ceil(T / 2), T * 3),
  },

  // ── 8: Início adiado no último momento ──────────────────────────────────────
  {
    label: `8 · Winding Down — ETA aumentou para T+${T * 2}min (início adiado)`,
    durationSeconds: D,
    response: windingDownScene(T * 2, T * 4),
  },

  // ── 9: Manutenção ativa, previsão curta ─────────────────────────────────────
  {
    label: `9 · Maintenance — previsão ${T * 10}min`,
    durationSeconds: D,
    response: maintenanceScene(T * 10),
  },

  // ── 10: ETA da manutenção aumentado ─────────────────────────────────────────
  {
    label: `10 · Maintenance — ETA aumentou para ${T * 20}min`,
    durationSeconds: D,
    response: maintenanceScene(T * 20),
  },

  // ── 11: Segundo ciclo: agendado ─────────────────────────────────────────────
  {
    label: `11 · Scheduled — 2º ciclo, T+${T * 6}min`,
    durationSeconds: D,
    response: scheduledScene(T * 6, T * 12),
  },

  // ── 12: Segundo ciclo: winding down ─────────────────────────────────────────
  {
    label: `12 · Winding Down — 2º ciclo, T+${Math.ceil(T / 2)}min`,
    durationSeconds: D,
    response: windingDownScene(Math.ceil(T / 2), T * 6),
  },

  // ── 13: Erro de rede #1 — service mantém último estado, backoff inicia ───────
  {
    label: '13 · Erro de rede #1 (service mantém winding_down, backoff inicia)',
    durationSeconds: D,
    response: errorScene(),
  },

  // ── 14: Erro de rede #2 — backoff dobra ─────────────────────────────────────
  {
    label: '14 · Erro de rede #2 (backoff aumenta)',
    durationSeconds: D,
    response: errorScene(),
  },

  // ── 15: Recuperação após erros — entra direto em manutenção ─────────────────
  {
    label: `15 · Maintenance — recuperação após erros, previsão ${T * 15}min`,
    durationSeconds: D,
    response: maintenanceScene(T * 15),
  },

  // ── 16: Manutenção com ETA muito longo ──────────────────────────────────────
  {
    label: `16 · Maintenance — ETA longo ${T * 30}min (duração estendida)`,
    durationSeconds: D,
    response: maintenanceScene(T * 30),
  },

  // ── 17: Estabilidade pós-manutenção (sem redirect ainda) ────────────────────
  {
    label: '17 · Normal — pós-manutenção estável (sem redirect ainda)',
    durationSeconds: D,
    response: normalScene(),
  },

  // ── 18: ÚLTIMO — fim de manutenção com redirect (mantém aqui para não matar o roteiro) ──
  // Esta é a última cena. O componente de manutenção detecta a transição para
  // normal e dispara window.location.href → hard reload → roteiro reinicia.
  // Isso é esperado: todos os cenários já foram cobertos antes desta cena.
  {
    label: '18 · Normal — FIM DO ROTEIRO (redirect para login após manutenção)',
    durationSeconds: 999_999,
    response: normalScene(),
  },
];

// ─── EXPORT PRINCIPAL ─────────────────────────────────────────────────────────

export function getMockMaintenanceStatus(): MaintenanceResponse | null {
  return getCurrentScene().response();
}
